/**
 * Client for re-identification risk (Annexure H Part I 114). Wire shapes
 * mirror apps/api-controller/src/modules/discovery/reidentification/service.ts.
 * Kept out of api.ts so parallel work on that file does not collide with this.
 */

import { apiFetch } from './api';

export type RiskBand = 'high' | 'medium' | 'low';
export type ColumnRole = 'direct' | 'quasi' | 'other';

export interface RiskColumn {
  column: string;
  label: string;
  classifiedRole: ColumnRole;
  role: ColumnRole;
  added: boolean;
  removed: boolean;
}

export interface RiskSettings {
  quasiAdded: string[];
  quasiRemoved: string[];
  sensitiveColumn: string | null;
  sampleLimit: number;
  monitor: boolean;
  lastMonitoredAt: string | null;
  lastMonitoredOutcome: string | null;
  updatedBy: string | null;
  updatedAt: string | null;
}

export interface RiskAssessment {
  id: string;
  directIdentifiers: Array<{ column: string; label: string; nonNullRecords: number }>;
  quasiIdentifiers: Array<{ column: string; label: string; added: boolean }>;
  sensitiveColumn: string | null;
  rowsAssessed: number;
  tableRows: number;
  sampled: boolean;
  samplePercent: number | null;
  sampleLimit: number;
  groups: number;
  k: number | null;
  atRiskRecords: number;
  atRiskShare: number;
  l: number | null;
  band: RiskBand;
  trigger: 'manual' | 'scheduled';
  assessedBy: string;
  assessedAt: string;
}

export interface RiskView {
  assetId: string;
  unavailableReason: string | null;
  source: { systemKey: string; schema: string; table: string } | null;
  columns: RiskColumn[];
  settings: RiskSettings;
  rule: string;
  /** Newest first. */
  history: RiskAssessment[];
}

export type RiskSettingsInput = Partial<Pick<RiskSettings, 'quasiAdded' | 'quasiRemoved' | 'sensitiveColumn' | 'sampleLimit' | 'monitor'>>;

const base = (assetId: string) => `/v1/discovery/reidentification-risk/assets/${encodeURIComponent(assetId)}`;

export async function getRiskView(assetId: string): Promise<RiskView> {
  return (await apiFetch<{ risk: RiskView }>(base(assetId))).risk;
}

export async function updateRiskSettings(assetId: string, input: RiskSettingsInput): Promise<RiskSettings> {
  return (await apiFetch<{ settings: RiskSettings }>(`${base(assetId)}/settings`, { method: 'PUT', body: JSON.stringify(input) })).settings;
}

export async function assessRisk(assetId: string): Promise<RiskAssessment> {
  return (await apiFetch<{ assessment: RiskAssessment }>(`${base(assetId)}/assessments`, { method: 'POST' })).assessment;
}
